<script setup lang="ts">
  import type { HpButton } from '#components'

  const props = defineProps<{
    launcherText: string
    icon?: string
    hideLauncher?: boolean
    variant?:  "primary" | "secondary" | "minimal" | "none" | "danger" | "secondary-danger" | "success" | undefined
  }>()

  const customizationObject = {
    transcript: {
      hideDisplayNames: true,
      eventNames: {
        customer: "You",
        agent: "Healthpilot"
      }
    },
    composer: {
      disableEmojiPicker: true,
      disableCustomerAttachment: true,
    },
  }

  const customStyles = {
    global: {
      frameHeight: '680px',
      frameWidth: '360px',
    }
  }

  const launchBehavior = `{
  alwaysHideLaunchButton: true,
  skipIconButtonAndAutoLaunch: true,
  programmaticLaunch: (function(launchCallback) {
        console.log('LAUNCH WIDGET ', launchWidgetBtn);
        var launchWidgetBtn = document.getElementById('amazon-connect-chat-launcher-btn');
        if (launchWidgetBtn) {
            launchWidgetBtn.addEventListener('click', launchCallback);
            window.onunload = function() {
            launchWidgetBtn?.removeEventListener('click', launchCallback);
            return;
            }
        }
    })
    }`

  useHead({
    script: [
      {
        type: 'text/javascript',
        innerHTML: " (function(w, d, x, id){\n" +
  "    s=d.createElement('script');\n" +
  "    s.src='https://d10hxo0w83tp48.cloudfront.net/amazon-connect-chat-interface-client.js';\n" +
  "    s.async=1;\n" +
  "    s.id=id;\n" +
  "    d.getElementsByTagName('head')[0].appendChild(s);\n" +
  "    w[x] =  w[x] || function() { (w[x].ac = w[x].ac || []).push(arguments) };\n" +
  "  })(window, document, 'amazon_connect', '8b041a90-e1a7-4797-afd2-7f71da4fb47b');\n" +
  "  amazon_connect('styles', { " +
          "iconType: 'CHAT', " +
          "openChat: {  color: '#ffffff', backgroundColor: '#123456' }, " +
          "closeChat: { color: '#ffffff', backgroundColor: '#123456'}});\n" +
  `  amazon_connect('customStyles', ${JSON.stringify(customStyles)});\n` +
  `  amazon_connect('customizationObject', ${JSON.stringify(customizationObject)});\n` +
  "  amazon_connect('snippetId', 'QVFJREFIaDhDQnJIRDNrbGlxb01PbmxZbjFOVzhZNWVpdEZ3VEoxV09TeFRGQ3ZDb2dIOEFSSlAxaERiZ1VoTHJqS2VuSkh0QUFBQWJqQnNCZ2txaGtpRzl3MEJCd2FnWHpCZEFnRUFNRmdHQ1NxR1NJYjNEUUVIQVRBZUJnbGdoa2dCWlFNRUFTNHdFUVFNREVmOVl3VHQxd3FUdXJmdkFnRVFnQ3VKSVlMQm9CZ0pIV0hSN05KZ3FLaHZDblhybHVhZzVSaERGWE14VE52djZ4L29wR0I2a3NxS3ZXVVU6OjhzUnRoUDE4SCsxSElGTkh5LytEM3VrRU4rWDJtb1dXdmlYOFkrbDNVSnFiNndtOEZhM3FkLytMTDFoNSs1Y3Z1S01OTEpXWTN2MDZubUVoSlZ0OU8vbnBiQkQ0bzFYMDg3cWowVXZKZ1FjZXRWeGVZS3hWLzRId3ROQzlHREpPTDB3c1RSV0ZlSXFRc2drK3dFS01OT3dHR3NXTkpFMD0=');\n" +
  "  amazon_connect('supportedMessagingContentTypes', [ 'text/plain', 'application/vnd.amazonaws.connect.message.interactive', 'application/vnd.amazonaws.connect.message.interactive.response' ]);" +
  "  amazon_connect('customerChatInterfaceUrl', 'https://trsconnect-chat-interface.s3.us-west-2.amazonaws.com/amazon-connect-chat-interface.js');"
          + `  amazon_connect('customLaunchBehavior', ${launchBehavior});`
      }
    ]
  })

  const ID = 'amazon-connect'

  const chatLauncher = ref<InstanceType<typeof HpButton> | null>(null)

  const { $root } = useNuxtApp()

  const show = () => {
    document.getElementById('amazon-connect-chat-launcher-btn')?.click()
  }

  const hide = () => {
    (document.getElementById("amazon-connect-chat-widget-iframe") as HTMLIFrameElement)
      ?.contentWindow
      ?.connect.ChatSession.disconnect();
  }

  const toggle = () => {
    //TODO: is this possible with connect
  }

  onMounted(() => {
    $root.on(`trigger:${makeRootChatEvent('show', ID)}`, (args?: any) => {
      show()
    })

    $root.on(`trigger:${makeRootChatEvent('hide', ID)}`, (args?: any) => {
      hide()
    })

    $root.on(`trigger:${makeRootChatEvent('toggle', ID)}`, (args?: any) => {
      toggle()
    })
  })

  onBeforeUnmount(() => {
    // @ts-ignore
    $root.off(`trigger:${makeRootChatEvent('show', ID)}`)
    $root.off(`trigger:${makeRootChatEvent('hide', ID)}`)
    $root.off(`trigger:${makeRootChatEvent('toggle', ID)}`)
  })
</script>

<template>
  <HpButton
    :class="{ 'invisible': hideLauncher }"
    ref="chatLauncher"
    id="amazon-connect-chat-launcher-btn"
    :variant="variant ?? 'secondary'"
    :icon
  >
    {{launcherText}}
  </HpButton>
</template>

<style>
  #amazon-connect-widget-frame {
    @apply h-1/2 w-2/3 sm:h-[680px] sm:w-[360px] sm:absolute sm:right-0 sm:bottom-[4rem];
  }

  #amazon-connect-widget-frame.show {
    @apply rounded-[1.5rem];
  }

  #amazon-connect-widget-frame:not(.show) {
    @apply h-0;
  }

  button#amazon-connect-open-widget-button, button#amazon-connect-close-widget-button {
    display: none !important;
  }
</style>